import { useTranslation } from 'react-i18next';

import { Icon } from '@/shared/ui/icon';

interface InheritableCellProps {
  value: number | null;
  /** `false` = hodnota se přebírá ze společné v záhlaví. */
  isManual: boolean;
  readOnly?: boolean;
  /** Psaní do zděděné buňky ji automaticky přepne na vlastní hodnotu. */
  onChange: (value: number | null, isManual: boolean) => void;
}

/**
 * Buňka se sdílenou / ruční hodnotou — stejná pro cenu i kapacitu.
 *
 * **Zděděná buňka není disabled a jde do ní psát.** Psaním se termín automaticky přepne do vlastního
 * nastavení a buňka změní zabarvení; klik na ikonu posuvníků vrátí společnou hodnotu.
 */
interface DefinitionCellProps {
  value: number | null;
  readOnly?: boolean;
  title: string;
  /** Jednotka za vstupem — `pax` = cena za osobu, `grp` = částka za celou skupinu. */
  unit?: 'pax' | 'grp';
  onChange: (value: number | null) => void;
}

/**
 * Buňka hodnoty, která **je** definicí — nemá z čeho dědit, takže ani přepínač zdroje.
 *
 * Používá se pro společné hodnoty seriálu v záhlaví a pro ceny termínu s vlastní definicí: ten má
 * vlastní tickety, takže cena na ticketu je přímo jeho cena a ruční přepis by byl dvojí evidence.
 * Prázdný `op-tc-sp` drží šířku po chybějícím tlačítku, aby sloupce lícovaly se zděděnými buňkami;
 * jednotka (`op-tc-u`) ho nahradí tam, kde se vyplní.
 */
export function DefinitionCell({ value, readOnly, title, unit, onChange }: DefinitionCellProps) {
  const { t } = useTranslation('venua-planner');

  return (
    <span className="op-tc">
      <input
        className="op-tc-in"
        type="text"
        inputMode="numeric"
        readOnly={readOnly}
        value={value ?? ''}
        title={title}
        onChange={(event) => onChange(parseAmount(event.target.value))}
      />
      {unit ? (
        <span className="op-tc-u">{t(`transportScheme.cell.unit.${unit}`)}</span>
      ) : (
        <span className="op-tc-sp" />
      )}
    </span>
  );
}

export function InheritableCell({ value, isManual, readOnly, onChange }: InheritableCellProps) {
  const { t } = useTranslation('venua-planner');

  return (
    <span className="op-tc" {...(isManual ? { 'data-manual': '' } : {})}>
      <input
        className="op-tc-in"
        type="text"
        inputMode="numeric"
        readOnly={readOnly}
        value={value ?? ''}
        title={t(
          isManual ? 'transportScheme.cell.manualHint' : 'transportScheme.cell.inheritedHint'
        )}
        onChange={(event) => {
          // Zápis do zděděné buňky = přechod na vlastní hodnotu.
          onChange(parseAmount(event.target.value), true);
        }}
      />
      <button
        type="button"
        className="op-tc-src"
        disabled={readOnly}
        title={t(
          isManual ? 'transportScheme.cell.resetHint' : 'transportScheme.cell.inheritedHint'
        )}
        onClick={() => {
          if (isManual) onChange(null, false);
        }}
      >
        <Icon name={isManual ? 'tune' : 'share'} className="h-3 w-3" />
      </button>
    </span>
  );
}

/** Desetinná čárka i tečka; nečíselný vstup se zahodí, prázdný znamená „nezadáno". */
function parseAmount(raw: string): number | null {
  const value = raw.replace(',', '.').trim();
  if (value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}
